import React, { useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react';
import userStore from '../../Stores/User';
import { linkNewEmail, deleteUser } from '../../audiblogsApi';
import mixpanel from 'mixpanel-browser';
import { useHistory } from 'react-router-dom';
import loadingIcon from "../../Assets/loading.gif";
import queryString from 'query-string';
import Checkout from '../Checkout';
import { Modal, mergeStyleSets } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { createStripeSessionAndRedirectToCheckout } from '../../stripeLogic';

const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column',
    backgroundColor: 'rgb(66 59 74)',
    width: '300px',
    padding: '10px',
    fontFamily: 'Cabin',
    fontSize: '18px',
    textAlign: 'center',
    minHeight: '108px',
    borderRadius: '14px',
  },
});

const Account = observer(() => {
  const { logOut, idToken, isSignedIn, user, creditsHd, creditsSd } = userStore;
  const [deletedData, setDeletedData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    mixpanel.track('Open Account Page');
  }, []);

  useEffect(() => {
    if (isSignedIn === false && deletedData === false) {
      history.push(`/signin`);
    } else if (isSignedIn === false && deletedData === true) {
      history.push(`/bye`);
    }
  }, [isSignedIn, idToken]);

  const showCharacterPurchaseModal = () => {
    const CharacterPurchaseModal = () => {
      const [charAmount, setCharAmount] = useState(1);
      const pricePerMillion = 15;
      const totalPrice = (charAmount * pricePerMillion).toFixed(2);
      
      return (
        <>
          <div style={{ color: 'white', marginBottom: '15px', fontWeight: 'bold' }}>
            Purchase Additional Characters
          </div>
          <div style={{ color: 'white', marginBottom: '20px' }}>
            Purchase additional characters that will be used for any overages beyond your plan's allotment.
          </div>
          
          <div style={{ marginBottom: '20px', textAlign: 'left' }}>
            <div style={{ color: 'white', marginBottom: '10px' }}>
              Select amount to purchase:
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={charAmount}
              onChange={(e) => setCharAmount(Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              color: 'white',
              marginTop: '5px'
            }}>
              <div>{charAmount}M characters</div>
              <div>${totalPrice}</div>
            </div>
            <div style={{ 
              color: '#4CAF50', 
              fontSize: '14px',
              marginTop: '5px' 
            }}>
              ~{Math.round((charAmount * 1000000 / 10000) * 10.5)} minutes of audio
            </div>
          </div>
          
          <button
            onClick={async () => {
              try {
                setIsLoading(true);
                const response = await createStripeSessionAndRedirectToCheckout(userStore.idToken, {
                  mode: 'payment',
                  amount: charAmount * pricePerMillion * 100,
                  currency: 'usd',
                  metadata: {
                    type: 'character_purchase',
                    characters: charAmount * 1000000
                  }
                });
                // No need to check response.url anymore since stripe.redirectToCheckout handles the redirect
              } catch (error) {
                console.error('Error creating character purchase session:', error);
                setModalContent({
                  title: "Error",
                  message: "Unable to process payment. Please try again or contact support.",
                });
              } finally {
                setIsLoading(false);
              }
            }}
            className="clickable"
            style={{
              backgroundColor: '#1976d2',
              border: 'none',
              color: 'white',
              borderRadius: '9px',
              marginTop: '14px',
              marginRight: '10px',
              fontSize: '16px',
              padding: '8px 16px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {isLoading ? (
              <img src={loadingIcon} style={{ height: '25px' }} alt="loading..." />
            ) : (
              `Purchase for $${totalPrice}`
            )}
          </button>
          
          <button
            onClick={() => {
              window.Intercom('show');
              hideModal();
            }}
            className="clickable"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid lightgrey',
              color: 'white',
              borderRadius: '9px',
              marginTop: '14px',
              fontSize: '16px',
              padding: '2px 8px',
              outline: 'none',
            }}
          >
            Contact Support
          </button>
        </>
      );
    };

    setModalContent({
      customContent: <CharacterPurchaseModal />
    });
    showModal();
  };

  return (
    <div className="center">
      <Modal
        isOpen={isModalOpen && !isLoading}
        onDismiss={hideModal}
        isBlocking={false}
        isDarkOverlay={true}
        containerClassName={contentStyles.container}
      >
        {modalContent?.customContent}
      </Modal>

      {user === null ? (
        <img src={loadingIcon} style={{ height: '16px' }} />
      ) : (
        <>
          {creditsHd && creditsHd > 0 && (
            <div>{creditsHd} HD hours</div>
          )}
    
          {creditsSd && creditsSd > 0 && (
            <div>{creditsSd} SD hours</div>
          )}

          {user.extra_credits > 0 && (
            <div style={{ marginBottom: '10px' }}>
              {Number(user.extra_credits).toLocaleString()} additional characters available
            </div>
          )}

          {(user.stripe_customer_id || user.customer_id) && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={showCharacterPurchaseModal}
                  className="transparent-button mobile"
                  style={{ width: "245px", cursor: "pointer" }}
                >
                  Buy More Characters
                </button>
                <a 
                  href="https://help.audioread.com/en/articles/11783511-how-extra-characters-work" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  <span style={{ border: '1px solid currentColor', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', lineHeight: 1, fontSize: '14px' }}>?</span>
                </a>
              </div>
            </div>
          )}

          {user.subscriptions && (
            user.subscriptions.hd || user.subscriptions.sd && (
              <>
                <div style={{ marginTop: '10px', width: "257px" }}>
                  <Link className="transparent-button mobile" to={`/subscribe`}>
                    <span>Buy Credits</span>
                  </Link>
                </div>
              </>
            )
          )}

          {userStore.customerPortalUrl && (
            <a
              href={userStore.customerPortalUrl}
              target="_blank"
              className="transparent-button mobile"
              style={{ width: "245px" }}
            >
              Manage Billing
            </a>
          )}
        </>
      )}

      <div className="account-sub-div">
        <div>
          <div style={{ marginTop: '10px'}}>
            <Link className="transparent-button mobile" onClick={logOut}>
              Log Out
            </Link>
          </div>
          {deletedData && <div>Account removed. Loging out...</div>}
        </div>
        <div style={{ marginTop: '10px'}}>
          <Link to="/add" style={{ textDecoration: 'none', color: 'inherit' }}>
            Go Back
          </Link>
        </div>
      </div>
    </div>
  );
});

export default Account;
